import Workflow, { WorkflowAction } from 'models/Workflow';

import { isCheckableManualRecord, readEligibility } from './eligibility/eligibilityData';
import { VALUE_NAMES } from './constants';

export type CoverageCorrection = { facetId: string; payerChanged: boolean };

// The completed steps a correction would ripple into (design §9) — what the restart-consent step
// lists before anything saves. Shared by Edit Demographics and the Patient Demographics card's
// "Use this value", so the two can't disagree about what a change re-runs.
//
// `names` are referral-level value names that changed; `coverages` the coverages whose payer or
// member ID changed. A coverage correction re-runs that coverage's own payer search and
// verification — scoped, so fixing one coverage doesn't ask to re-run the other's paid check.
// Empty on a terminal run: the API rejects restarts there.
//
// A hand-documented benefits record is left out wherever the API leaves it standing, so the list
// never asks consent for a restart that won't happen: a referral-level edit never restarts it, and a
// member ID change only does when its payer can be checked. A payer change does restart it — the
// payer search runs again, and a new pick re-runs the check.
export function impactedActionsFor(
  workflow: Workflow,
  { names = [], coverages = [] }: { names?: string[]; coverages?: CoverageCorrection[] }
): WorkflowAction[] {
  if (workflow.isTerminal) return [];

  const fromValues = (names.length > 0 ? workflow.impactedActionsBy(names) : []).filter(
    (action) => action.rerunnable !== false
  );
  const fromCoverages = coverages.flatMap(({ facetId, payerChanged }) =>
    workflow.impactedActionsBy([VALUE_NAMES.PAYER_NAME, VALUE_NAMES.MEMBER_ID], [facetId]).filter((action) => {
      if (payerChanged || action.rerunnable !== false) return true;
      const { manual } = readEligibility(workflow, facetId);
      return !manual || isCheckableManualRecord(manual);
    })
  );

  return [...fromValues, ...fromCoverages].filter(
    (action, index, all) => all.findIndex((other) => other.id === action.id) === index
  );
}
